import React from "react"; // Adjust path as needed
import DatePicker from "react-datepicker"; // You might need to install this package
import "react-datepicker/dist/react-datepicker.css";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect"; // Adjust path as needed
import ProjectSelect from "../../atoms/ProjectSelect/ProjectSelect"; // Adjust path as needed
import { useDispatch, useSelector } from "react-redux";
import { getFilterTaskAPI } from "../../../Redux/Apis/taskApi";
import moment from "moment";
import { checkPermission, permissionType } from "../../../app/utils/common/Functions";
import DailyHoursSendMail from "../../molecules/DailyHoursSendMail/DailyHoursSendMail";
import "./TaskFilter.scss";

const isQuillEmpty = (html) => {
  const s = String(html ?? "").trim();
  if (!s) return true;
  if (s === "<p><br></p>" || s === "<p></p>") return true;
  return !s.replace(/<[^>]+>/g, "").trim();
};

const TASK_FILTER_EMPLOYEE_EXTRA_OPTIONS = [{ label: "All", value: "all" }];

const DAILY_EXTRAS_FIELDS = [
  { key: "inProgressTasks", label: "In-progress tasks", section: "Tasks", richText: true },
  { key: "plannedForTomorrow", label: "Planned for tomorrow", section: "Tasks", richText: true },
  { key: "issueBlocker", label: "Issue / Blocker", section: "Blockers / Dependencies" },
  { key: "dependency", label: "Dependency", section: "Blockers / Dependencies" },
  { key: "supportRequiredFrom", label: "Support Required From", section: "Blockers / Dependencies" },
  { key: "meetingsDiscussions", label: "Meetings attended / discussions", section: "Notes / Additional Updates" },
  { key: "clientFeedback", label: "Client feedback", section: "Notes / Additional Updates" },
  { key: "deploymentNotes", label: "Deployment updates or important notes", section: "Notes / Additional Updates" },
];

const DailyExtrasDisplay = ({ dailyExtras }) => {
  if (!dailyExtras) return null;

  const sections = [...new Set(DAILY_EXTRAS_FIELDS.map((f) => f.section))];
  const hasContent = DAILY_EXTRAS_FIELDS.some((f) =>
    f.richText ? !isQuillEmpty(dailyExtras[f.key]) : dailyExtras[f.key]?.trim()
  );
  if (!hasContent) return null;

  return (
    <div className="daily-extras-display mt-4 pt-4 border-t border-gray-200">
      {sections.map((sectionTitle) => {
        const fields = DAILY_EXTRAS_FIELDS.filter((f) => f.section === sectionTitle);
        const sectionHasContent = fields.some((f) =>
          f.richText ? !isQuillEmpty(dailyExtras[f.key]) : dailyExtras[f.key]?.trim()
        );
        if (!sectionHasContent) return null;

        return (
          <div key={sectionTitle} className="mb-4">
            <h4 className="font-semibold text-sm text-gray-800 mb-2">{sectionTitle}</h4>
            <div className="grid grid-cols-1 gap-2">
              {fields.map(({ key, label, richText }) => {
                const value = dailyExtras[key];
                if (richText ? isQuillEmpty(value) : !value?.trim()) return null;
                return (
                  <div key={key} className="text-sm">
                    <span className="font-medium text-gray-700">{label}: </span>
                    {richText ? (
                      <div
                        className="text-gray-600 task-description-readonly !min-h-0 !mt-1"
                        dangerouslySetInnerHTML={{ __html: value }}
                      />
                    ) : (
                      <span className="text-gray-600 whitespace-pre-wrap">{value}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TaskFilter = () => {
  const [startDate, setStartDate] = React.useState(new Date());
  const [endDate, setEndDate] = React.useState(new Date());
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [selectedEmployee, setSelectedEmployee] = React.useState("me");
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch();
  const { filteredTasks } = useSelector((state) => state.task);
  const canSelectEmployees = checkPermission(permissionType.USER_MANAGEMENT, "read");
  // Sample data - you would normally fetch this from an API

  // Function to fetch task data based on filters
  const fetchTaskData = () => {
    setLoading(true);

    let payload = {
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
    };
    if (selectedEmployee) {
      payload.userId = selectedEmployee;
    }
    if (selectedProject) {
      payload.projectId = selectedProject.value;
    }
    // In a real application, you would make an API call here
    dispatch(getFilterTaskAPI(payload))
      .unwrap()
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  // Apply filters
  const handleApplyFilters = () => {
    fetchTaskData();
  };

  return (
    <div className="task-filter-container">
      <div className="task-filter-page-header mb-4">
        <h1 className="page-title">My Task</h1>
        <p className="page-subtitle">Stay organized, complete tasks, and achieve goals.</p>
      </div>
      <div className="task-filter-header">
        <div className="filter-controls card-border card-body mt-2">
          <div className="date-filters flex gap-4 w-full items-end justify-center ">
            <div className="date-picker flex flex-col gap-2 w-1/4 ">
              <label>Start </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                maxDate={new Date()}
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
              />
            </div>

            <div className="date-picker flex flex-col gap-2 w-1/4">
              <label>End</label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
              />
            </div>
            <div className="select-container w-1/4 flex">
              <ProjectSelect
                value={selectedProject}
                onChange={setSelectedProject}
                className="w-full p-0"
                showLabel={false}
                isClearable
              />
            </div>

            {canSelectEmployees ? (
              <div className="select-container w-1/4">
                <EmployeeSelect
                  value={selectedEmployee}
                  onSelectEmployee={(employeeId) =>
                    setSelectedEmployee(employeeId ?? "me")
                  }
                  className="!min-h-[44px] !w-full"
                  extraOptions={TASK_FILTER_EMPLOYEE_EXTRA_OPTIONS}
                  allowClear
                  excludeCeoCmo
                />
              </div>
            ) : null}
          </div>

          <div className="filter-actions mt-4 flex justify-end mr-10">
            <button
              onClick={handleApplyFilters}
              className="button bg-btn-primary text-white px-4 py-2 rounded cursor-pointer"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="p-4 text-center">
          <p>Loading task data...</p>
        </div>
      )}

      {!loading && filteredTasks && (
        <div className="task-results mt-4">
          {Object.keys(filteredTasks).length === 0 ? (
            <p className="text-center py-8 card-border card-body">
              No tasks found for the selected filters.
            </p>
          ) : (
            <div className="task-days-container">
              {Object.keys(filteredTasks).map((date) => (
                <div
                  key={date}
                  className="day-container mb-6 card-border overflow-hidden"
                >
                  <div className="date-header p-3 font-bold">
                    {new Date(date).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>

                  {(Array.isArray(filteredTasks[date]) ? filteredTasks[date] : []).map((userData, userIndex) => (
                    <div key={userIndex} className="user-tasks p-4 border-t">
                      <div className="user-tasks__header">
                        <div className="user-info flex items-center gap-3 mb-3 min-w-0">
                          {userData.userDetails.profileImage ? (
                            <img
                              src={userData.userDetails?.profileImage}
                              alt={`${userData.userDetails?.firstname} ${userData.userDetails?.lastname}`}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="text-black w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                              {userData.userDetails.firstname?.charAt(0)}
                              {userData.userDetails.lastname?.charAt(0)}
                            </div>
                          )}
                          <div>
                            <h3 className="font-semibold text-black">
                              {userData.userDetails.firstname}{" "}
                              {userData.userDetails.lastname}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {userData.userDetails.email}
                            </p>
                          </div>
                        </div>
                        <DailyHoursSendMail
                          date={date}
                          userDetails={userData.userDetails}
                          taskDetails={userData.taskDetails}
                          dailyExtras={userData.dailyExtras}
                          className="user-tasks__send-mail"
                        />
                      </div>

                      <div className="task-list grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {userData.taskDetails.map((task) => (
                          <div key={task.taskId} className="task-card p-3 rounded">
                            <div className="task-card__meta">
                              <span className="task-card__project">
                                {task?.projectName} - {task?.projectCategory}
                              </span>
                              <span
                                className={`task-card__status ${task.status === "Completed"
                                  ? "task-card__status--completed"
                                  : "task-card__status--pending"
                                  }`}
                              >
                                {task?.status}
                              </span>
                            </div>
                            <div
                              className="task-card__description"
                              dangerouslySetInnerHTML={{
                                __html: task.description,
                              }}
                            />
                            <div className="task-card__footer">
                              <span>
                                Duration: {Math.floor(task?.duration / 60)}h{" "}
                                {task?.duration % 60}m
                              </span>
                              <span>Task #{task?.taskId}</span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <DailyExtrasDisplay dailyExtras={userData.dailyExtras} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskFilter;
